import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";
import { GreekWatermark } from "@/components/GreekWatermark";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FOUNDING_ELIGIBLE = new Set([
  "membership_foundation_monthly",
  "membership_unlimited_monthly",
  "membership_unlimited_yearly",
]);

const PLANS = [
  {
    name: "Day Pass",
    price: "£30",
    period: "one-time",
    lookup_key: "day_pass",
    features: ["Single drop-in class", "Full access to that class", "No commitment"],
  },
  {
    name: "Foundation",
    price: 129,
    period: "/ month",
    lookup_key: "membership_foundation_monthly",
    features: ["8 classes per month", "Access to Open Gym", "Community events"],
  },
  {
    name: "Unlimited",
    price: 189,
    period: "/ month",
    lookup_key: "membership_unlimited_monthly",
    features: ["Unlimited classes", "Priority booking", "Nutrition check-ins", "2 guest passes / month"],
    featured: true,
  },
  {
    name: "Unlimited Annual",
    price: 1890,
    period: "/ year",
    lookup_key: "membership_unlimited_yearly",
    features: ["Everything in Unlimited", "2 months free vs monthly", "Locked-in rate"],
  },
];

export default function Membership() {
  const [loadingKey, setLoadingKey] = useState(null);
  const [offer, setOffer] = useState(null);
  const [claimFounding, setClaimFounding] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [details, setDetails] = useState({ name: "", email: "", phone: "" });
  const [notes, setNotes] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    api
      .get("/founding-offer")
      .then(({ data }) => setOffer(data))
      .catch(() => setOffer({ active: false, remaining: 0 }));
  }, []);

  useEffect(() => {
    if (user) setDetails({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
  }, [user]);

  const offerActive = offer?.active;
  const discountAmount = offer?.amount_off ? offer.amount_off / 100 : 20;

  const openRequest = (plan) => {
    setSelectedPlan(plan);
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setLoadingKey(selectedPlan.lookup_key);
    try {
      const { data } = await api.post("/memberships/request", {
        plan_key: selectedPlan.lookup_key,
        name: details.name,
        email: details.email,
        phone: details.phone,
        notes,
        claim_founding: claimFounding && offerActive && FOUNDING_ELIGIBLE.has(selectedPlan.lookup_key),
      });
      toast.success(data.message);
      setSelectedPlan(null);
      setNotes("");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div className="pt-28 sm:pt-36 pb-20 sm:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <GreekWatermark letter="Μ" className="top-10 right-0" />
      <Reveal className="text-center max-w-2xl mx-auto">
        <p className="font-accent italic text-gold text-lg mb-3">Membership</p>
        <h1 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-6xl">Pick your path to Elysium</h1>
        <p className="text-smoke mt-4">Pick a plan, send us your details, and we'll be in touch to arrange payment directly — nothing to pay online.</p>
      </Reveal>

      <Reveal delay={0.05} className="max-w-3xl mx-auto mt-10">
        <div
          className="border border-gold bg-gold/10 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left"
          data-testid="founding-50-banner"
        >
          <div>
            <p className="text-gold font-anton uppercase tracking-wider text-lg">Founding 50 — Launch Offer</p>
            <p className="text-smoke text-sm mt-1">
              {offerActive
                ? `The first 50 members lock in £${discountAmount} off Unlimited & Foundation for life, plus a free Elysium kit bundle.`
                : "This launch offer has now closed — thank you to everyone who claimed a founding spot."}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {offer ? (
              <span
                className={`border text-xs uppercase tracking-widest px-4 py-2 ${
                  offerActive ? "border-gold text-gold" : "border-line text-smoke"
                }`}
                data-testid="founding-50-spots-remaining"
              >
                {offerActive ? `${offer.remaining} of ${offer.max_redemptions} spots left` : "Offer Closed"}
              </span>
            ) : (
              <Loader2 className="h-4 w-4 text-gold animate-spin" />
            )}
            {offerActive && (
              <label className="flex items-center gap-2 text-xs text-smoke cursor-pointer" data-testid="founding-50-claim-toggle">
                <Switch checked={claimFounding} onCheckedChange={setClaimFounding} />
                Claim my spot
              </label>
            )}
          </div>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
        {PLANS.map((plan, i) => {
          const eligible = FOUNDING_ELIGIBLE.has(plan.lookup_key);
          const applyDiscount = offerActive && claimFounding && eligible && typeof plan.price === "number";
          return (
            <Reveal key={plan.lookup_key} delay={i * 0.06} className="h-full">
              <div
                className={`flex flex-col h-full border p-8 relative ${
                  plan.featured ? "border-gold bg-gold/5 drop-shadow-[0_0_25px_rgba(154,137,104,0.15)]" : "border-line bg-surface"
                }`}
                data-testid={`membership-plan-${plan.lookup_key}`}
              >
                {plan.featured && <span className="text-xs uppercase tracking-widest text-gold mb-3">Most Popular</span>}
                {applyDiscount && (
                  <span className="absolute top-0 right-0 bg-gold text-void text-[10px] font-bold uppercase tracking-widest px-2 py-1" data-testid={`founding-badge-${plan.lookup_key}`}>
                    Founding Rate
                  </span>
                )}
                <h3 className="font-anton uppercase text-cream text-2xl">{plan.name}</h3>
                <div className="mt-3 mb-6">
                  {applyDiscount ? (
                    <>
                      <span className="text-smoke text-lg line-through mr-2">£{plan.price}</span>
                      <span className="font-anton text-4xl text-gold">£{plan.price - discountAmount}</span>
                    </>
                  ) : (
                    <span className="font-anton text-4xl text-gold">{typeof plan.price === "number" ? `£${plan.price}` : plan.price}</span>
                  )}
                  <span className="text-smoke text-sm ml-1">{plan.period}</span>
                </div>
                <ul className="space-y-3 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-smoke">
                      <Check className="h-4 w-4 text-gold mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                  {applyDiscount && (
                    <li className="flex items-start gap-2 text-sm text-gold">
                      <Check className="h-4 w-4 text-gold mt-0.5 shrink-0" /> Free Elysium founding kit bundle
                    </li>
                  )}
                </ul>
                <Button
                  onClick={() => openRequest(plan)}
                  disabled={loadingKey === plan.lookup_key}
                  className={`rounded-none mt-8 font-bold uppercase tracking-wider w-full ${
                    plan.featured
                      ? "bg-gold text-void hover:bg-gold-hover"
                      : "bg-transparent border border-gold text-gold hover:bg-gold hover:text-void"
                  }`}
                  data-testid={`choose-plan-button-${plan.lookup_key}`}
                >
                  {loadingKey === plan.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request This Plan"}
                </Button>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Dialog open={!!selectedPlan} onOpenChange={(open) => !open && setSelectedPlan(null)}>
        <DialogContent className="bg-surface border-line text-cream rounded-none max-w-md" data-testid="membership-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-anton uppercase tracking-tight text-cream text-2xl">
              Request {selectedPlan?.name}
            </DialogTitle>
            <DialogDescription className="text-smoke">
              We'll email your details to the Elysium team — they'll be in touch to arrange payment directly. Nothing to pay online.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRequest} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-smoke">Name</label>
                <Input
                  required
                  value={details.name}
                  onChange={(e) => setDetails({ ...details, name: e.target.value })}
                  className="rounded-none bg-void border-line text-cream mt-2 h-11 focus-visible:ring-gold focus-visible:border-gold"
                  data-testid="membership-request-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-smoke">Phone</label>
                <Input
                  required
                  type="tel"
                  value={details.phone}
                  onChange={(e) => setDetails({ ...details, phone: e.target.value })}
                  className="rounded-none bg-void border-line text-cream mt-2 h-11 focus-visible:ring-gold focus-visible:border-gold"
                  data-testid="membership-request-phone-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-smoke">Email</label>
              <Input
                required
                type="email"
                value={details.email}
                onChange={(e) => setDetails({ ...details, email: e.target.value })}
                className="rounded-none bg-void border-line text-cream mt-2 h-11 focus-visible:ring-gold focus-visible:border-gold"
                data-testid="membership-request-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-smoke">Anything we should know? (optional)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="rounded-none bg-void border-line text-cream mt-2 focus-visible:ring-gold focus-visible:border-gold"
                data-testid="membership-request-notes-input"
              />
            </div>
            <Button
              type="submit"
              disabled={!!loadingKey}
              className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider w-full h-11"
              data-testid="membership-request-submit-button"
            >
              {loadingKey ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send My Details"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
